import React, { useState, useEffect } from 'react';
import {
  BellOutlined,
  DownOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  SearchOutlined,
  MenuOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import {
  Layout,
  Button,
  Input,
  Switch,
  Dropdown,
  Space,
  Avatar,
  Badge,
  message,
  AutoComplete,
} from 'antd';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../redux/slice/userSlice';
import { apiRequest } from '../service/apiRequest';

import { customThemes } from '../themeConfig';


const { Header } = Layout;

interface ThemeStyles {
  headerBg: string;
  siderBg: string;
  footerBg: string;
}

interface HeaderComponentProps {
  collapsed: boolean;
  setCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  darkMode: boolean;
  setDarkMode: React.Dispatch<React.SetStateAction<boolean>>;
  themeStyles: ThemeStyles;
  isMobile: boolean;
}

const HeaderComponent: React.FC<HeaderComponentProps> = ({
  collapsed,
  setCollapsed,
  darkMode,
  setDarkMode,
  // themeStyles,
  isMobile,
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
   const themeStyles = darkMode ? customThemes.dark : customThemes.light;

  // Notifications
  const [notifications, setNotifications] = useState<string[]>([
    'New comment on your post.',
    'You have a new follower.',
    'Your profile was viewed.',
  ]);

  const [searchSuggestions, setSearchSuggestions] = useState<string[]>([
    'Ant Design',
    'React',
    'TypeScript',
    'JavaScript',
    'Frontend Development',
  ]);

  // Redux hooks
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isDashboard = location.pathname === '/' || location.pathname === '/dashboard';
  const user = useSelector((state: any) => state.user?.user); // Adjust the type accordingly

  // Logout function
  const useLogout = () => {
    const handleLogout = async () => {
      try {
        if (user) {
          await apiRequest({
            method: 'get',
            url: `admin-logout`,
          });
        }
      } catch (error) {
        console.error('Logout failed:', error);
      } finally {
        dispatch(logout());
        navigate('/login', { replace: true });
      }
    };
    return handleLogout;
  };

  const handleLogout = useLogout();

  // Fullscreen toggle
  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Track fullscreen state
  useEffect(() => {
    const handler = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // Handle notification click
  const handleNotificationClick = () => {
    setNotifications([]);
    message.info('You have no new notifications!');
  };

  // Search autocomplete
  const handleSearch = (value: string) => {
    const filtered = searchSuggestions.filter((suggestion) =>
      suggestion.toLowerCase().includes(value.toLowerCase())
    );
    return filtered.map((suggestion) => ({ value: suggestion }));
  };

  // Notification dropdown items
  const notificationMenuItems = notifications.length
    ? notifications.map((note, index) => ({
      key: String(index),
      label: note,
    }))
    : [{ key: 'none', label: 'No notifications', disabled: true }];

  // Profile dropdown items
  const profileMenuItems = [
    {
      key: 'profile',
      label: <Link to="/profile">My Profile</Link>,
    },
    {
      key: 'change-password',
      label: <Link to="/change-password">Change Password</Link>,
    },
    {
      key: 'logout',
      label: 'Logout',
      onClick: handleLogout,
    },
  ];

  return (
    <Header
      style={{
        padding: '0 16px',
        backgroundColor: themeStyles.headerBg,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {/* Universal Back Button (hidden on dashboard) */}
        {!isDashboard && (
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(-1)}
            style={{ fontSize: 16 }}
            title="Go Back"
          />
        )}

        {/* Sidebar Toggle */}
        <Button
          type="text"
          icon={<MenuOutlined />}
          onClick={() => setCollapsed(!collapsed)}
          style={{ fontSize: 16 }}
        />
      </div>

      <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>

        {/* Search Bar */}
        {!isMobile && (
          <AutoComplete
            style={{ width: 380, marginRight: 16, padding: 0, border: 0}}
            options={handleSearch('')}
            onSearch={handleSearch}
          >
            <Input prefix={<SearchOutlined />} placeholder="Search..." allowClear 
            style={{  borderRadius: 4 ,width: '104%',  }}
            />
          </AutoComplete>
        )}

        {/* Right Controls */}
        {/* Fullscreen Toggle */}
        <Button
          type="text"
          icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
          onClick={toggleFullScreen}
        />

        {/* Notifications */}
        <Badge count={notifications.length} offset={[-10, 10]}>
          <Dropdown menu={{ items: notificationMenuItems }} trigger={['click']}>
            <Button
              type="text"
              icon={<BellOutlined />}
              onClick={handleNotificationClick}
            />
          </Dropdown>
        </Badge>

        {/* Theme Switch */}
        <Switch
          checkedChildren="Dark"
          unCheckedChildren="Light"
          checked={darkMode}
          onChange={setDarkMode}
        />

        {/* Profile Dropdown */}
        <Dropdown menu={{ items: profileMenuItems }}>
          <a onClick={(e) => e.preventDefault()}>
            <Space>
              <Avatar size="small" src={`${process.env.REACT_APP_FILE_URL}uploads/users/profile-image/${user.user_image}`} />
              {!isMobile && <span>{user?.user_name}</span>}
              <DownOutlined />
            </Space>
          </a>
        </Dropdown>
      </div>
    </Header>
  );
};

export default HeaderComponent;
